import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationWorkflowService } from 'src/app/services/navigation-workflow.service';
import { SpeetalsDatabaseService } from 'src/app/services/common/speetalsDatabase/speetals-database.service';
import { WebsiteAnalyzeService } from 'src/app/services/common/website-analyze/website-analyze.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule, ChartComponent, ApexAxisChartSeries, ApexChart, ApexPlotOptions, ApexXAxis, ApexDataLabels, ApexLegend, ApexTitleSubtitle } from 'ng-apexcharts';
import { forkJoin, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ShowDetailsWorkflowComponent } from '../show-details-workflow/show-details-workflow.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SenderVarService } from 'src/app/services/senderVar.service';
import { Subscription } from 'rxjs';
import { ConstantService } from 'src/app/shared/constant/constant.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  xaxis: ApexXAxis;
  colors: string[];
  legend: ApexLegend;
  title: ApexTitleSubtitle;
};

interface CruxPageData {
  url: string;
  p75Lcp?: number;
  p75Cls?: number;
  p75Inp?: number;
  uxr?: number;
  updateAt?: Date;
  device?: string;
  error?: string;
}

@Component({
    selector: 'app-crux-workflow',
    standalone: true,
    imports: [CommonModule, NgApexchartsModule, MatSelectModule, FormsModule, MatIconModule, MatRadioModule],
    templateUrl: './crux-workflow.component.html',
    styleUrls: ['./crux-workflow.component.scss']
})
export class CruxWorkflowComponent implements OnInit, AfterViewInit, OnDestroy {
  userId: string;
  workflows: any[] = [];
  selectedWorkflow: any;
  cruxData: CruxPageData[] = [];


  validCruxData: CruxPageData[] = [];

  loading = true;
  errorMsg = '';
  chartErrorMsg = '';
  selectedDevice: string = 'desktop';
  device: string = 'desktop';

  private root!: am5.Root;
  private projectRouteSub?: Subscription;

  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  constructor(
    private workflowService: NavigationWorkflowService,
    private speetalsDatabaseService: SpeetalsDatabaseService,
    private websiteAnalyzeService: WebsiteAnalyzeService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private senderVarService: SenderVarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userId =
      localStorage.getItem(ConstantService.localStorageKeys.userId) ||
      localStorage.getItem('userId') ||
      '';
    const id$ = this.route.parent?.paramMap ?? this.route.paramMap;
    const nav$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    );
    this.projectRouteSub = merge(id$, nav$)
      .pipe(
        map(() =>
          [
            this.senderVarService.resolveProjectIdFrom(this.route, this.router) ||
              '',
            this.router.url.split('?')[0],
          ].join('|'),
        ),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadCruxWorkflows());
  }

  ngAfterViewInit(): void {
    // On peut appeler la méthode de construction du chart si les données sont déjà disponibles.
    if (!this.loading) {
      this.buildAmChartFunnel();
    }
  }



  loadCruxWorkflows(): void {
    if (!this.userId) {
      this.errorMsg = 'No userId found in localStorage.';
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }
    this.loading = true;
    this.errorMsg = '';
    this.chartErrorMsg = '';

    const routeProjectId =
      this.senderVarService.syncProjectIdFromRoute(this.route, this.router) ??
      this.senderVarService.resolveProjectIdFrom(this.route, this.router);

    this.websiteAnalyzeService.getWebsiteAnalyze(this.userId).subscribe({
      next: (websites) => {
        const siteList = Array.isArray(websites) ? websites : [websites];
        const scoped =
          routeProjectId != null && routeProjectId !== ''
            ? siteList.filter((w) => w?.id === routeProjectId)
            : siteList;
        const workflowRequests = scoped.map((website) =>
          this.workflowService.getNavigationWorkflows(website.id).pipe(
            catchError((err) => {
              console.error(
                `Error loading workflows for project ${website.id}:`,
                err,
              );
              return of([]);
            }),
          ),
        );

        if (workflowRequests.length === 0) {
          this.workflows = [];
          this.selectedWorkflow = undefined;
          this.cruxData = [];
          this.validCruxData = [];
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }

        forkJoin(workflowRequests).subscribe({
          next: (results) => {
            const allWorkflows = ([] as any[]).concat(...results);
            this.workflows = allWorkflows.filter((wf) => wf.type === 'CRUX');
            if (this.workflows.length > 0) {
              this.selectedWorkflow = this.workflows[0];
              this.loadCruxDataForWorkflow(this.selectedWorkflow);
            } else {
              this.selectedWorkflow = undefined;
              this.cruxData = [];
              this.validCruxData = [];
              this.loading = false;
              this.cdr.markForCheck();
            }
          },
          error: (err) => {
            console.error('Error loading workflows:', err);
            this.errorMsg = 'Error loading CRUX workflows.';
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
      },
      error: (err) => {
        console.error('Error loading websites:', err);
        this.errorMsg = 'Error loading websites.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  selectWorkflow(wf: any): void {
    this.selectedWorkflow = wf;
    this.loadCruxDataForWorkflow(wf);
  }

  loadCruxDataForWorkflow(workflow: any): void {
    if (!workflow || !workflow.pages?.length) {
      this.cruxData = [];
      this.validCruxData = [];
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }
    const pages = workflow.pages;
    const workflowProjectId = workflow.projectId || workflow.id;

    const observables = pages.map((p: { url: string }) =>
      this.speetalsDatabaseService.getMostRecentDocByUrlAndUser(workflowProjectId, p.url).pipe(
        catchError(err => {
          console.error('Error fetching docs for url=', p.url, err);
          return of({ desktop: { url: p.url, error: 'Error fetching CRUX data' }, mobile: { url: p.url, error: 'Error fetching CRUX data' } });
        }),
        map((result: any) => {
          return {
            url: p.url,
            desktop: result.desktop,
            mobile: result.mobile
          };
        })
      )
    );

    if (observables.length === 0) {
      this.cruxData = [];
      this.validCruxData = [];
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    forkJoin(observables).subscribe({
      next: (results: any[]) => {
        this.cruxData = results;
        this.validCruxData = this.cruxData.filter(item => !item[this.selectedDevice]?.error);

        const invalidUrls = this.cruxData.filter(item => item[this.selectedDevice]?.error).map(item => item.url);
        this.chartErrorMsg = invalidUrls.length ? 'The following URLs have no CRUX data for ' + this.selectedDevice + ': ' + invalidUrls.join(', ') : '';

        this.loading = false;
        this.cdr.markForCheck();

        setTimeout(() => {
          this.buildAmChartFunnel();
        });
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  setDeviceUrl(event: Event): void {
    const target = event.target as HTMLInputElement;
    const deviceValue = target.value;
    this.device = deviceValue;
    this.selectedDevice = deviceValue;
    this.buildAmChartFunnel();
  }

  buildAmChartFunnel(): void {
    if (this.root) {
      this.root.dispose();
    }

    this.root = am5.Root.new("chartdiv");
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    const chart = this.root.container.children.push(
      am5percent.SlicedChart.new(this.root, {
        layout: this.root.verticalLayout,
        interactive: true,
      })
    );

    const chartData = this.cruxData.map(d => ({
      category: d.url,
      value: d[this.selectedDevice]?.uxr || 0
    }));

    const series = chart.series.push(
      am5percent.FunnelSeries.new(this.root, {
        valueField: "value",
        categoryField: "category",
        orientation: "horizontal",
        alignLabels: false,
        bottomRatio: 1,
        interactive: true,
      })
    );
    series.data.setAll(chartData);

    series.slices.template.setAll({
      interactive: true,
      cursorOverStyle: "pointer",
      tooltipText: "{category}: {value}%"
    });

    series.labels.template.setAll({
      text: "UXR: {value}%",
      interactive: false,
      rotation: 0,
      textAlign: "start"
    });

    series.events.on("datavalidated", () => {
      series.dataItems.forEach((dataItem) => {
        const slice = dataItem.get("slice");
        if (slice) {
          slice.setAll({
            interactive: true,
            cursorOverStyle: "pointer"
          });
          slice.events.on("click", () => {
            const dataContext = dataItem.dataContext as { category: string; value: number };
            this.openModal(dataContext.category);
          });
        }
      });
    });




    const legend = chart.children.push(
      am5.Legend.new(this.root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 15,
        marginBottom: 15
      })
    );
    legend.labels.template.set("text", "{category}");
    legend.valueLabels.template.setAll({ forceHidden: true });
    legend.data.setAll(series.dataItems);

    series.appear();
    chart.appear(1000, 100);
  }


  openModal(url: string): void {
    const item = this.cruxData.find(d => d.url === url);
    if (!item) {
      return;
    }

    const deviceData = item[this.selectedDevice];
    if (!deviceData || deviceData.error) {
      return;
    }

    const lcpData = {
      good: deviceData.goodLcp,
      need: deviceData.needsImprovementLcp,
      poor: deviceData.poorLcp,
      p75: deviceData.p75Lcp
    };
    const clsData = {
      good: deviceData.goodCls,
      need: deviceData.needsImprovementCls,
      poor: deviceData.poorCls,
      p75: deviceData.p75Cls
    };
    const inpData = {
      good: deviceData.goodInp,
      need: deviceData.needsImprovementInp,
      poor: deviceData.poorInp,
      p75: deviceData.p75Inp
    };

    const modalData = {
      url: deviceData.url,
      device: deviceData.device,
      lcpData,
      clsData,
      inpData
    };

    this.dialog.open(ShowDetailsWorkflowComponent, {
      width: '1000px',
      height: '500px',
      data: modalData
    });
  }


  ngOnDestroy(): void {
    this.projectRouteSub?.unsubscribe();
    // Détruire la racine du chart pour éviter les fuites
    if (this.root) {
      this.root.dispose();
    }
  }

  navigateToMyAccount() {
    this.router.navigate(['/home/myAccount'], { queryParams: { tab: 3 } });
  }
}
